import { useParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { eventDetails } from "@/mocks/eventDetails";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import EventHero from "./components/EventHero";
import EventInfo from "./components/EventInfo";
import EventContent from "./components/EventContent";
import EventRegistrationForm from "./components/EventRegistrationForm";
import RelatedEvents from "./components/RelatedEvents";

export default function EventDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const event = eventDetails.find((e) => e.slug === slug);

  useEffect(() => {
    if (!event) {
      navigate("/");
      return undefined;
    }
    window.scrollTo(0, 0);

    const siteUrl = import.meta.env.VITE_SITE_URL || "";
    const eventUrl = `${siteUrl}/event/${event.slug}`;

    const schema = {
      "@context": "https://schema.org",
      "@type": "Event",
      "name": event.title,
      "description": event.description || event.title,
      "url": eventUrl,
      "image": event.image,
      "startDate": event.date,
      "location": {
        "@type": "Place",
        "name": event.location || "台灣",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "TW"
        }
      },
      "organizer": {
        "@type": "Organization",
        "name": "娛樂紅歡密 Rappy me",
        "url": siteUrl,
        "sameAs": [
          "https://www.instagram.com/rappy.meme/",
          "https://www.facebook.com/Red.shrimp.close/"
        ]
      },
      "isPartOf": {
        "@type": "WebSite",
        "name": "娛樂紅歡密 Rappy me",
        "url": siteUrl
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "首頁",
            "item": siteUrl
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": event.title,
            "item": eventUrl
          }
        ]
      }
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "schema-event";
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    const prevTitle = document.title;
    document.title = `${event.title}｜娛樂紅歡密 Rappy me`;

    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content") || "";
    if (metaDesc) {
      metaDesc.setAttribute("content", `${event.title} — 娛樂紅歡密 Rappy me 精心策劃的台中活動，立即報名參加！`);
    }

    return () => {
      const el = document.getElementById("schema-event");
      if (el) el.remove();
      document.title = prevTitle;
      if (metaDesc) metaDesc.setAttribute("content", prevDesc);
    };
  }, [event, navigate]);

  if (!event) return null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <EventHero event={event} />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Left: Content */}
          <div className="lg:col-span-2">
            <EventContent event={event} />
          </div>

          {/* Right: Sticky Info Card */}
          <div className="lg:col-span-1">
            <EventInfo event={event} />
          </div>
        </div>
      </div>

      {/* Related Events */}
      <RelatedEvents currentEvent={event} />

      {/* Registration Form */}
      <EventRegistrationForm event={event} />

      {/* Back to Home */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-gray-500 hover:text-red-600 transition-colors cursor-pointer text-sm font-medium"
        >
          <i className="ri-arrow-left-line" />
          返回首頁
        </button>
      </div>

      <Footer />
    </div>
  );
}
